/**
 * DocumentFilters Component - Filter controls for documents
 */

import React from 'react'
import { Search, Filter, X } from 'lucide-react'
import { Input, Select, Badge } from '../ui'

const DocumentFilters = ({
  categories = [],
  statuses = [],
  onFilterChange,
  onSearchChange,
  searchQuery = '',
  selectedCategory = '',
  selectedStatus = '',
  selectedCritical = false,
  className = '',
  ...props
}) => {
  const [showFilters, setShowFilters] = React.useState(false)

  const handleClear = () => {
    onFilterChange?.({ category: '', status: '', critical: false })
    onSearchChange?.('')
  }

  const hasFilters = selectedCategory || selectedStatus || selectedCritical || searchQuery

  return (
    <div className={`space-y-3 ${className}`} {...props}>
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-9 pr-4 py-2.5 border border-neutral-200 rounded-lg bg-white focus:outline-none focus:border-brand transition-colors text-sm"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`
            px-4 py-2.5 text-sm font-semibold rounded-lg border transition-colors
            ${showFilters || hasFilters ? 'border-brand bg-brand-soft text-brand' : 'border-neutral-200 text-muted hover:text-ink'}
          `}
        >
          <Filter size={16} className="inline mr-1.5" />
          Filters
          {hasFilters && (
            <Badge variant="primary" size="sm" className="ml-1.5">
              {[selectedCategory, selectedStatus, selectedCritical, searchQuery].filter(Boolean).length}
            </Badge>
          )}
        </button>

        {/* Clear */}
        {hasFilters && (
          <button
            onClick={handleClear}
            className="text-sm text-muted hover:text-ink transition-colors px-2"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg animate-fade-in space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Category filter */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => onFilterChange?.({ category: e.target.value, status: selectedStatus, critical: selectedCritical })}
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white focus:outline-none focus:border-brand"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => onFilterChange?.({ category: selectedCategory, status: e.target.value, critical: selectedCritical })}
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white focus:outline-none focus:border-brand"
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Critical filter */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
                Critical
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCritical}
                  onChange={(e) => onFilterChange?.({ category: selectedCategory, status: selectedStatus, critical: e.target.checked })}
                  className="w-4 h-4 rounded border-neutral-300 text-brand focus:ring-brand"
                />
                <span className="text-sm text-ink">Show only critical documents</span>
              </label>
            </div>
          </div>

          {/* Active filters */}
          {hasFilters && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-neutral-200">
              {searchQuery && (
                <Badge variant="default" size="sm" className="flex items-center gap-1">
                  Search: {searchQuery}
                  <button onClick={() => onSearchChange?.('')} className="hover:text-error">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              {selectedCategory && (
                <Badge variant="default" size="sm" className="flex items-center gap-1">
                  Category: {categories.find(c => c.id === selectedCategory)?.name}
                  <button onClick={() => onFilterChange?.({ category: '', status: selectedStatus, critical: selectedCritical })} className="hover:text-error">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              {selectedStatus && (
                <Badge variant="default" size="sm" className="flex items-center gap-1">
                  Status: {selectedStatus}
                  <button onClick={() => onFilterChange?.({ category: selectedCategory, status: '', critical: selectedCritical })} className="hover:text-error">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              {selectedCritical && (
                <Badge variant="error" size="sm" className="flex items-center gap-1">
                  Critical Only
                  <button onClick={() => onFilterChange?.({ category: selectedCategory, status: selectedStatus, critical: false })} className="hover:text-error">
                    <X size={12} />
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

DocumentFilters.displayName = 'DocumentFilters'

export default DocumentFilters